/**
 * Yoopta 에디터와 백엔드 API 간 데이터 변환 유틸리티
 */

/**
 * 블럭 타입별 데이터 추출
 */
const extractBlockData = (block) => {
  if (!block.value || !block.value[0]) {
    return { text: '' };
  }

  const firstValue = block.value[0];
  const text = firstValue.children?.[0]?.text || '';

  switch (block.type) {
    case 'Paragraph':
    case 'HeadingOne':
    case 'HeadingTwo':
    case 'HeadingThree':
    case 'Blockquote':
      return { text };
      
    case 'BulletedList':
    case 'NumberedList':
    case 'TodoList':
      return { 
        text,
        listType: block.type
      };
      
    case 'Image':
      return {
        src: firstValue.props?.src || '',
        alt: firstValue.props?.alt || '',
        width: firstValue.props?.sizes?.width || firstValue.props?.width || 'auto',
        height: firstValue.props?.sizes?.height || firstValue.props?.height || 'auto'
      };
      
    case 'File':
      return {
        src: firstValue.props?.src || '',
        name: firstValue.props?.name || '',
        size: firstValue.props?.size || 0
      };
      
    case 'Link':
      return {
        text,
        url: firstValue.props?.url || '',
        target: firstValue.props?.target || '_self'
      };
      
    case 'Code':
      return {
        code: text,
        language: firstValue.props?.language || 'javascript'
      };
      
    case 'Table':
      return {
        rows: firstValue.props?.rows || [],
        columns: firstValue.props?.columns || []
      };
      
    case 'Callout':
      return {
        text,
        theme: firstValue.props?.theme || 'info'
      };
      
    default:
      return { text };
  }
};

/**
 * 블럭 타입 이름 정규화 (첫 글자 대문자)
 */
const capitalizeBlockType = (type) => {
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

/**
 * 백엔드 데이터를 Yoopta 블럭 형식으로 변환
 */
const convertBlockData = (blockData, blockType) => {
  const text = blockData.text || '';
  const elementId = `element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // 이미지 블록인 경우 특별 처리
  if (blockType === 'image') {
    return [
      {
        id: elementId,
        type: 'image',
        children: [{ text: '' }],
        props: {
          src: blockData.src || '',
          alt: blockData.alt || '',
          sizes: {
            width: blockData.width || 'auto',
            height: blockData.height || 'auto'
          }
        }
      }
    ];
  }
  
  // 파일 블록인 경우 특별 처리
  if (blockType === 'file') {
    return [
      {
        id: elementId,
        type: 'file',
        children: [{ text: '' }],
        props: {
          src: blockData.src || '',
          name: blockData.name || '',
          size: blockData.size || 0
        }
      }
    ];
  }
  
  // 다른 블록 타입들 처리
  return [
    {
      id: elementId,
      type: 'paragraph',
      children: [{ text }]
    }
  ];
};

/**
 * Yoopta 블럭 데이터를 백엔드 API 형식으로 변환
 * @param {Object} yooptaBlocks - Yoopta 에디터의 블럭 데이터
 * @returns {Object|null} 백엔드 API 형식의 데이터
 */
export const yooptaToBackend = (yooptaBlocks) => {
  console.log('[YooptaDataConverter] yooptaToBackend 호출:', yooptaBlocks);
  
  if (!yooptaBlocks || typeof yooptaBlocks !== 'object' || Object.keys(yooptaBlocks).length === 0) {
    return null;
  }

  try {
    // 블록들을 meta.order 순서로 정렬
    const sortedBlocks = Object.values(yooptaBlocks).sort((a, b) => {
      const orderA = a.meta?.order || 0;
      const orderB = b.meta?.order || 0;
      return orderA - orderB;
    });
    
    console.log('[YooptaDataConverter] 🔢 정렬된 블록 순서:', sortedBlocks.map(block => ({
      id: block.id,
      type: block.type,
      order: block.meta?.order || 0,
      isImage: block.type === 'Image',
      isFile: block.type === 'File'
    })));
    
    // 이미지/파일 블록들의 순서 특별 확인
    const imageFileBlocks = sortedBlocks.filter(block => block.type === 'Image' || block.type === 'File');
    if (imageFileBlocks.length > 0) {
      console.log('[YooptaDataConverter] 🖼️📁 이미지/파일 블록들만 따로 확인:', imageFileBlocks.map(block => ({
        id: block.id,
        type: block.type,
        order: block.meta?.order || 0
      })));
    }

    const blocks = sortedBlocks.map((block, index) => {
      const extractedData = extractBlockData(block);
      
      // 이미지/파일 블록인 경우 상세 로깅
      if (block.type === 'Image') {
        console.log('[YooptaDataConverter] 🖼️ 이미지 블록 변환:', {
          blockId: block.id,
          blockType: block.type,
          originalOrder: block.meta?.order || 0,
          newIndex: index,
          originalBlock: block,
          extractedData: extractedData
        });
      }
      
      if (block.type === 'File') {
        console.log('[YooptaDataConverter] 📁 파일 블록 변환:', {
          blockId: block.id,
          blockType: block.type,
          originalOrder: block.meta?.order || 0,
          newIndex: index,
          originalBlock: block,
          extractedData: extractedData
        });
      }
      
      return {
        id: block.id,
        type: block.type.toLowerCase(),
        data: extractedData,
        order: block.meta?.order || index  // order 정보 보존
      };
    });

    const result = {
      blocks,
      version: "2.0",
      time: Date.now()
    };
    
    console.log('[YooptaDataConverter] 백엔드 변환 결과:', result);
    return result;
  } catch (error) {
    console.error('Yoopta → 백엔드 변환 실패:', error);
    return null;
  }
};

/**
 * 백엔드 API 형식을 Yoopta 블럭 데이터로 변환
 * @param {Object} originalBlock - 백엔드 API의 originalBlock 데이터
 * @returns {Object|null} Yoopta 에디터 형식의 블럭 데이터
 */
export const backendToYoopta = (originalBlock) => {
  console.log('[YooptaDataConverter] backendToYoopta 호출:', originalBlock);
  
  if (!originalBlock || !originalBlock.blocks || !Array.isArray(originalBlock.blocks)) {
    console.log('[YooptaDataConverter] 백엔드 데이터가 유효하지 않음');
    return null;
  }

  try {
    const yooptaBlocks = {};
    
    // 백엔드 블록을 order 순서로 정렬 (order 필드가 있으면 사용, 없으면 배열 순서)
    const sortedBlocks = originalBlock.blocks.sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : originalBlock.blocks.indexOf(a);
      const orderB = b.order !== undefined ? b.order : originalBlock.blocks.indexOf(b);
      return orderA - orderB;
    });
    
    console.log('[YooptaDataConverter] 🔢 백엔드 블록 정렬 순서:', sortedBlocks.map((block, index) => ({
      id: block.id,
      type: block.type,
      savedOrder: block.order,
      arrayIndex: originalBlock.blocks.indexOf(block),
      finalOrder: block.order !== undefined ? block.order : originalBlock.blocks.indexOf(block),
      isImage: block.type === 'image',
      isFile: block.type === 'file'
    })));
    
    // 이미지/파일 블록들의 백엔드 순서 특별 확인
    const imageFileBackendBlocks = sortedBlocks.filter(block => block.type === 'image' || block.type === 'file');
    if (imageFileBackendBlocks.length > 0) {
      console.log('[YooptaDataConverter] 🖼️📁 백엔드 이미지/파일 블록들만 따로 확인:', imageFileBackendBlocks.map(block => ({
        id: block.id,
        type: block.type,
        savedOrder: block.order,
        finalOrder: block.order !== undefined ? block.order : originalBlock.blocks.indexOf(block)
      })));
    }
    
    sortedBlocks.forEach((block, index) => {
      const convertedData = convertBlockData(block.data, block.type);
      
      // 원본 order 정보 우선 사용, 없으면 정렬된 인덱스 사용
      const finalOrder = block.order !== undefined ? block.order : index;
      
      // 이미지/파일 블록인 경우 상세 로깅
      if (block.type === 'image') {
        console.log('[YooptaDataConverter] 🖼️ 이미지 블록 복원:', {
          blockId: block.id,
          blockType: block.type,
          savedOrder: block.order,
          finalOrder: finalOrder,
          originalData: block.data,
          convertedData: convertedData
        });
      }
      
      if (block.type === 'file') {
        console.log('[YooptaDataConverter] 📁 파일 블록 복원:', {
          blockId: block.id,
          blockType: block.type,
          savedOrder: block.order,
          finalOrder: finalOrder,
          originalData: block.data,
          convertedData: convertedData
        });
      }
      
      const yooptaBlock = {
        id: block.id,
        type: capitalizeBlockType(block.type),
        value: convertedData,
        meta: {
          order: finalOrder,
          depth: 0
        }
      };
      
      yooptaBlocks[block.id] = yooptaBlock;
    });

    console.log('[YooptaDataConverter] Yoopta 복원 결과:', yooptaBlocks);
    return yooptaBlocks;
  } catch (error) {
    console.error('백엔드 → Yoopta 변환 실패:', error);
    return null;
  }
};

/**
 * HTML을 간단한 백엔드 블럭 형식으로 변환 (fallback용)
 * @param {string} htmlContent - HTML 문자열
 * @returns {Object|null} 백엔드 API 형식의 데이터
 */
export const htmlToBackend = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return null;
  }

  try {
    // 간단한 HTML 파싱 (실제로는 더 정교한 파싱이 필요할 수 있음)
    const blockId = `paragraph-${Date.now()}`;
    
    return {
      blocks: [
        {
          id: blockId,
          type: 'paragraph',
          data: {
            text: htmlContent.replace(/<[^>]*>/g, '') // HTML 태그 제거
          }
        }
      ],
      version: "2.0",
      time: Date.now()
    };
  } catch (error) {
    console.error('HTML → 백엔드 변환 실패:', error);
    return null;
  }
};

/**
 * 데이터 유효성 검사
 */
export const validateYooptaBlocks = (blocks) => {
  return blocks && typeof blocks === 'object' && Object.keys(blocks).length > 0;
};

export const validateBackendBlocks = (originalBlock) => {
  return originalBlock && 
         originalBlock.blocks && 
         Array.isArray(originalBlock.blocks) && 
         originalBlock.blocks.length > 0;
};